import { fetchUsers, User } from "../user/userApi";

const CACHE_KEY = "drumly:notification-user-directory:v1";
export const NOTIFICATION_USER_CACHE_TTL_MS = 60 * 60 * 1000;
const PAGE_LIMIT = 100;
const MAX_PAGES = 100;

export interface NotificationUserOption {
  user_id: string;
  name: string;
  email: string;
  status: string;
}

export interface NotificationUserCache {
  users: NotificationUserOption[];
  fetchedAt: number;
  isStale: boolean;
}

interface StoredNotificationUserCache {
  version: 1;
  fetchedAt: number;
  users: NotificationUserOption[];
}

function toOption(user: User): NotificationUserOption | null {
  const userId = String(user.user_id || "").trim();
  if (!userId) return null;

  return {
    user_id: userId,
    name: String(user.display_name || user.name || "").trim(),
    email: String(user.email || "").trim(),
    status: String(user.status || (user.is_deleted ? "disabled" : "active")),
  };
}

function isUserOption(value: unknown): value is NotificationUserOption {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<NotificationUserOption>;
  return typeof candidate.user_id === "string" && candidate.user_id.length > 0;
}

export function readNotificationUserCache(
  includeStale = true
): NotificationUserCache | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw) as Partial<StoredNotificationUserCache>;
    if (
      stored.version !== 1 ||
      typeof stored.fetchedAt !== "number" ||
      !Array.isArray(stored.users)
    ) {
      window.localStorage.removeItem(CACHE_KEY);
      return null;
    }

    const users = stored.users.filter(isUserOption);
    const isStale = Date.now() - stored.fetchedAt >= NOTIFICATION_USER_CACHE_TTL_MS;
    if (isStale && !includeStale) return null;

    return { users, fetchedAt: stored.fetchedAt, isStale };
  } catch {
    window.localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function storeNotificationUsers(users: NotificationUserOption[]) {
  if (typeof window === "undefined") return;

  const payload: StoredNotificationUserCache = {
    version: 1,
    fetchedAt: Date.now(),
    users,
  };
  window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
}

export async function refreshNotificationUserCache(): Promise<NotificationUserCache> {
  const byId = new Map<string, NotificationUserOption>();
  let page = 1;
  let totalPages = 1;

  do {
    const response = await fetchUsers({
      page,
      limit: PAGE_LIMIT,
      sort_by: "name",
      sort_order: "asc",
    });

    response.items.forEach((user) => {
      const option = toOption(user);
      if (option) byId.set(option.user_id, option);
    });

    totalPages = Math.max(
      1,
      Number(response.meta?.total_pages) ||
        Math.ceil(Number(response.meta?.total || byId.size) / PAGE_LIMIT)
    );

    if (!response.items.length || response.items.length < PAGE_LIMIT) break;
    page += 1;
  } while (page <= totalPages && page <= MAX_PAGES);

  const users = Array.from(byId.values()).sort((left, right) =>
    (left.name || left.email || left.user_id).localeCompare(
      right.name || right.email || right.user_id,
      "tr",
      { sensitivity: "base" }
    )
  );

  storeNotificationUsers(users);
  return { users, fetchedAt: Date.now(), isStale: false };
}

